import {
  FOC_TOUR_LABEL_LOG,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_LOG_TOUR_HREF as HREF } from '../focTourHrefs';
import {
  FOC_POTENTIAL_DISPOSITION,
  FOC_REQUIRED_DISPOSITION,
  focLogTourPopoverHtml,
  requirement,
  type FocLogTourStepDef,
} from './focLogTourTypes';

export const FOC_LOG_TOUR_PARAM = 'focLogTour';
export const FOC_LOG_TOUR_HREF = HREF;
export const FOC_LOG_SEAT_SLUG = 'logistics-section-chief';
export { focLogTourPopoverHtml };

export function focLogTourSeatHref(incidentId: string): string {
  return `/iap/${incidentId}/seat/${FOC_LOG_SEAT_SLUG}?${FOC_LOG_TOUR_PARAM}=1`;
}

export const FOC_LOG_TOUR_STEPS: FocLogTourStepDef[] = [
  {
    id: 'sectionIntro',
    tour: 'log-workspace',
    title: FOC_TOUR_LABEL_LOG,
    description:
      'The Logistics Section workspace is a persistent capability for the Logistics Section Chief and authorized subordinate functions—available throughout activation, response, planning, execution, demobilization, and closeout.',
  },
  requirement({
    id: 'log001',
    requirementId: 'FOC-LOG-001',
    name: 'Logistics Section Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'PRATUS provides a persistent Logistics Section workspace for the Logistics Section Chief and authorized subordinate functions. The workspace supports incident logistics outside Planning P meetings and remains available throughout activation, response, operational planning, execution, demobilization, and closeout.',
    bullets: [
      'FOC-LOG-001a — Logistics Functional Areas: facilities; ICPs; bases; camps; staging; supply; equipment; property accountability; food/sustainment; lodging; sanitation; transportation; fleet; fueling; ground support; communications; IT; cybersecurity; medical; rehabilitation; support tasks; logistics resource requests; support-service agreements; constraints; demobilization support.',
      'FOC-LOG-001b — Logistics Forecasting: assess current and projected support requirements, consumption, timelines, source limitations, facilities capacity, supply-chain constraints, sustainment, and alternate sourcing.',
    ],
  }),
  requirement({
    id: 'log002',
    requirementId: 'FOC-LOG-002',
    name: 'Logistics Planning P Contributions',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports Logistics contributions to Initial Unified Command, Command and General Staff, Tactics, Planning Meeting, IAP Preparation/Approval, Operations Brief, execution, demobilization, and closeout.',
    bullets: [
      'FOC-LOG-002a — Tactical Feasibility: identify whether proposed tactics, resource requirements, facilities, transportation, communications, medical support, and sustainment can be supported for the next operational period.',
      'FOC-LOG-002b — Logistics Plans and IAP Products: creation, review, and submission of Logistics-related products including input to ICS 205, 206, 204, transportation/medical/communications plans, facilities information, supply/support plans, and other approved IAP components.',
    ],
  }),
  requirement({
    id: 'log003',
    requirementId: 'FOC-LOG-003',
    name: 'Logistics System Linkages',
    disposition: FOC_POTENTIAL_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports authorized data-sharing and workflow integration with CG-LIMS and VLS to reduce duplicate entry and enable approved visibility of inventory, parts, configuration data, asset availability, stock levels, and transit estimates. Activation depends on data-owner authorization, interface availability, C5I/security approval, and required agreements.',
  }),
  requirement({
    id: 'log004',
    requirementId: 'FOC-LOG-004',
    name: 'Logistics Demobilization and Closeout',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports resource recovery, equipment return, facility deactivation/restoration, transportation coordination, contract/support-agreement closeout, supply reconciliation, property accountability, documentation completion, and Logistics lessons learned.',
  }),
  requirement({
    id: 'log005',
    requirementId: 'FOC-LOG-005',
    name: 'Communications Unit Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Provides a Communications Unit workflow for communications-resource availability, incident communications planning, channel/system assignments, contact/access information, operational-period communications products, and changes affecting field assignments.',
    bullets: [
      'FOC-LOG-005a — ICS 217A to ICS 205/205A Data Flow: communications-resource information can develop ICS 217A, 205, and 205A; approved assignments map to applicable ICS 204 fields without duplicate entry where equivalent.',
      'FOC-LOG-005b — Communications Change Management: revise with version history and notify affected assignments/users when an approved change materially affects operations.',
    ],
  }),
  requirement({
    id: 'log006',
    requirementId: 'FOC-LOG-006',
    name: 'Medical Unit Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Provides a Medical Unit workflow to develop and maintain incident medical support information, including aid/medical locations, transportation/evacuation resources, receiving facilities, emergency procedures, contacts, and operational-period medical products.',
    bullets: [
      'FOC-LOG-006a — ICS 206 Data Reuse and Export: medical information populates the current approved ICS 206-CG and applicable field-assignment/briefing views without duplicate entry where equivalent; post-approval changes are versioned and distributed through authorized mechanisms.',
    ],
  }),
  requirement({
    id: 'log007',
    requirementId: 'FOC-LOG-007',
    name: 'Facilities Unit Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Provides a Facilities Unit workflow to establish, manage, map, and close incident facilities such as ICP, Base, camps, staging areas, JIC, family-assistance locations, and other incident facilities as applicable.',
    bullets: [
      'FOC-LOG-007a — Facility Needs Assessment / ICS 235: authorized Facilities personnel can document facility needs assessment and generate the current approved ICS 235-CG when applicable, reusing authoritative facility records and map geometry.',
    ],
  }),
  requirement({
    id: 'log008',
    requirementId: 'FOC-LOG-008',
    name: 'Supply, Ordering, and Support Requests',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports Logistics supply/ordering from identification of need through sourcing/ordering status, receipt, issue, accountability, and demobilization/return. Logistics users can receive routed ICS 213RR requests, identify supply source/status, record fulfillment, and communicate requester status.',
  }),
  requirement({
    id: 'log009',
    requirementId: 'FOC-LOG-009',
    name: 'Ground Support and Transportation',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports incident ground transportation and support planning, including vehicles/transport resources, movement requirements, pickup/delivery information, maintenance/support issues, transportation status, and demobilization/return coordination where applicable.',
  }),
  requirement({
    id: 'log010',
    requirementId: 'FOC-LOG-010',
    name: 'Food and Personnel Sustainment',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'log-workspace',
    description:
      'Supports incident food/lodging/sustainment planning at the level needed to identify supported population, location, period, requirement, provider/source, status, constraints, and unresolved actions.',
    bullets: [
      'FOC-LOG-010a — Service and Support Branch Visibility: the Logistics Section Chief can view status, blockers, requests, products, and assigned work across activated Service and Support Branch functions without requiring each subordinate function to be represented only as a Planning P meeting participant.',
    ],
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_LOG),
  },
];

export function focLogTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focLogTourStepIds(): string[] {
  return FOC_LOG_TOUR_STEPS.map((step) => step.id);
}
